// src/component/feedback/FeedbackTicketDetail.js
import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import FeedbackStatusChip from './FeedbackStatusChip';
import { FEEDBACK_CATEGORY_OPTIONS, FEEDBACK_STATUS_OPTIONS } from '../../constans/feedback';

const formatDate = (value) => {
  if (!value) return '-';
  const date = dayjs(value);
  return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '-';
};

const categoryLabel = (value) => {
  if (!value) return '-';
  const match = FEEDBACK_CATEGORY_OPTIONS.find((item) => item.value === value);
  return match ? match.label : value;
};

const isStaffMessage = (msg) => (msg?.direction || '').toLowerCase() === 'staff';

const MessageBubble = ({ message }) => {
  const staff = isStaffMessage(message);
  const align = staff ? 'flex-end' : 'flex-start';

  return (
    <Stack alignItems={align} sx={{ mb: 2 }}>
      <Box
        sx={{
          maxWidth: { xs: '92%', sm: '80%' },
          bgcolor: staff ? 'rgba(25,118,210,0.12)' : '#fff',
          borderRadius: 2,
          px: 2,
          py: 1.5,
          border: staff ? '1px solid rgba(25,118,210,0.2)' : '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent={staff ? 'flex-end' : 'flex-start'}
          alignItems="center"
          sx={{ mb: 0.5 }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: staff ? 'primary.main' : 'text.secondary',
            }}
          >
            {staff ? 'Destek Ekibi' : 'Müşteri'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(message.createdAt)}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            textAlign: staff ? 'right' : 'left',
            color: 'text.primary',
          }}
        >
          {message.message || '—'}
        </Typography>
      </Box>
    </Stack>
  );
};

export default function FeedbackTicketDetail({
  detail,
  loading,
  error,
  sending,
  updatingStatus,
  onSend,
  onRefresh,
  onLoadMore,
  hasMore,
  loadingMore,
  onStatusChange,
}) {
  const [draft, setDraft] = useState('');
  const [localStatus, setLocalStatus] = useState(detail?.thread?.status ?? '');
  const scrollRef = useRef(null);

  const thread = detail?.thread;
  const messages = detail?.messages ?? [];
  const totalMessages = detail?.total ?? messages.length;

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    if (!thread?.status) return;
    setLocalStatus(thread.status);
  }, [thread?.status]);

  const canSend = draft.trim().length > 0 && !sending;

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    const result = await onSend?.(text);
    if (result?.ok) {
      setDraft('');
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  };

  if (loading && !thread) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="rectangular" height={180} />
          <Skeleton variant="rectangular" height={120} />
        </Stack>
      </Paper>
    );
  }

  if (error && !thread) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!thread) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Ticket bulunamadı veya yüklenemedi.
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          justifyContent="space-between"
        >
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {thread.subject || 'Başlıksız Geri Bildirim'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {thread.customerEmail || '-'}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <FeedbackStatusChip status={thread.status} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {categoryLabel(thread.category)}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ color: 'text.secondary' }}>
              <Typography variant="caption">
                Oluşturulma: {formatDate(thread.createdAt)}
              </Typography>
              <Typography variant="caption">
                Son güncelleme: {formatDate(thread.updatedAt)}
              </Typography>
            </Stack>

            {!!thread.details && (
              <Box sx={{ bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2, p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Detaylar
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {thread.details}
                </Typography>
              </Box>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="feedback-status-selector">Durum</InputLabel>
              <Select
                labelId="feedback-status-selector"
                label="Durum"
                value={localStatus || ''}
                onChange={async (event) => {
                  const next = event.target.value;
                  setLocalStatus(next);
                  const res = await onStatusChange?.(next);
                  if (!res?.ok) {
                    setLocalStatus(thread.status);
                  }
                }}
                disabled={updatingStatus}
              >
                {FEEDBACK_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={onRefresh}
              startIcon={<ReplayIcon />}
              disabled={loading}
            >
              Yenile
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 3,
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 480,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Mesajlaşma
          </Typography>

          <Box
            ref={scrollRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 1,
              mr: -1,
              pb: 2,
            }}
          >
            {messages.length === 0 && (
              <Box
                sx={{
                  bgcolor: 'rgba(0,0,0,0.02)',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Henüz mesaj yok. Yanıt alanını kullanarak ilk mesajı gönderebilirsiniz.
                </Typography>
              </Box>
            )}

            {!messages.length && thread.details && (
              <Box
                sx={{
                  bgcolor: 'rgba(25,118,210,0.08)',
                  borderRadius: 2,
                  p: 2,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2" color="primary">
                  İlk Talep
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                  {thread.details}
                </Typography>
              </Box>
            )}

            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={onLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Yükleniyor…' : 'Daha fazla mesaj yükle'}
                </Button>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={1.5}>
            <TextField
              multiline
              minRows={3}
              placeholder="Yanıtınızı yazın..."
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              disabled={sending}
            />

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                variant="contained"
                endIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSend}
                disabled={!canSend}
              >
                Gönder
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            width: { xs: '100%', lg: 320 },
            p: 3,
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.06)',
          alignSelf: 'stretch',
        }}
      >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Bilet Bilgileri
          </Typography>
          <Stack spacing={1.5}>
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">Mesaj sayısı</Typography>
              <Typography variant="body2">{totalMessages}</Typography>
            </Stack>

            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">Oluşturan</Typography>
              <Typography variant="body2">{thread.customerEmail || '-'}</Typography>
            </Stack>

            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary">Kategori</Typography>
              <Typography variant="body2">{categoryLabel(thread.category)}</Typography>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
}
